import { useLazyQuery, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { MUTATION_UPLOAD_DOCUMENT } from "api/mutations/cases";
import { QUERY_CASE_DOCUMENTS } from "api/queries/cases";
import FormTitle from "components/Form/Title";
import PageContent from "components/Page/Content";
import Sidebar from "components/Page/Sidebar";
import usePageTitle from "hooks/usePageTitle";
import ResourceList from "components/Resources/ResourceList";
import { isCaseComplete } from "pages/ApplicationForm/utils";
import { Case as CaseType } from "ts/interfaces/common/case";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { APP_URLS } from "settings";
import MainStateContainer from "../../components/Page/MainStateContainer";
import DocsSidebar from "./components/DocsSidebar";
import FileError from "./components/FileError";
import UploadedFileList from "./components/UploadedFileList";
import UploadInput from "./components/UploadInput";
import {
  EMAIL_DOCUMENT_TO_US_COPY,
  MAX_COMBINED_SIZE_OF_FILE_UPLOADS,
  USER_ERROR_PREFIX,
} from "./consts";
import { documentTypes, linkDocumentTypes } from "./documentTypes";
import {
  ApplicationType,
  CaseDocument,
  DocumentUploadStateType,
} from "./types";

const CaseDocuments: React.FC = () => {
  const navigate = useNavigate();

  const { caseId: urlCaseId } = useParams<{
    caseId: string;
  }>();

  const [uploadDocument] = useMutation(MUTATION_UPLOAD_DOCUMENT);

  const [thisCase, setThisCase] = useState<CaseType | null>(null);
  usePageTitle(thisCase?.reference && `Documents - Case ${thisCase.reference}`);
  const [application, setApplication] = useState<ApplicationType | null>(null);
  const [documentState, setDocumentState] = useState<DocumentUploadStateType>({
    uploading: null,
    uploadError: {},
    documentsError: null,
  });

  const [
    getCaseDocuments,
    {
      data: caseDocuments,
      loading: caseDocumentsLoading,
      error: caseDocumentsError,
    },
  ] = useLazyQuery(QUERY_CASE_DOCUMENTS, { fetchPolicy: "no-cache" });

  useEffect(() => {
    if (caseDocuments?.error || caseDocumentsError) {
      setDocumentState((prev) => ({
        ...prev,
        documentsError: caseDocuments?.error || caseDocumentsError,
      }));
    }
  }, [caseDocuments?.error, caseDocumentsError]);

  useEffect(() => {
    if (urlCaseId && !thisCase) {
      getCaseDocuments({
        variables: {
          caseId: urlCaseId,
        },
      });
    }
  }, [urlCaseId, thisCase, getCaseDocuments]);

  useEffect(() => {
    if (!thisCase && caseDocuments?.data?.case) {
      // No longer allow editing of documents for completed cases
      if (isCaseComplete(caseDocuments.data.case?.status)) {
        return navigate(
          `${APP_URLS.CASE_MANAGEMENT}/${caseDocuments.data.case?.uuid}/`,
        );
      }
      setThisCase(caseDocuments.data.case);
      setApplication(
        caseDocuments.data.case.applications.find(
          (app: ApplicationType) => app.isLatestSubmission,
        ) ?? null,
      );
    }
  }, [thisCase, caseDocuments, setThisCase, setApplication, navigate]);

  const getExistingFiles = (documentType: string) => {
    if (!caseDocuments?.data?.case?.documents) {
      return [];
    }
    return caseDocuments.data.case.documents.filter(
      (doc: CaseDocument) => doc.kind === documentType,
    );
  };

  const handleError = (error: any, errorMessage: string) => {
    Sentry.captureException(error);
    if (error?.message && error.message.includes(USER_ERROR_PREFIX)) {
      return error.message.replace(USER_ERROR_PREFIX, "");
    }
    return errorMessage;
  };

  const updateExistingFilesState = async () => {
    if (!thisCase?.uuid) return;

    return getCaseDocuments({
      variables: {
        caseId: thisCase.uuid,
      },
    });
  };

  const resetDocumentStateErrors = (
    documentType: string,
    isUploading: boolean = false,
  ) => {
    setDocumentState((prev) => ({
      ...prev,
      uploading: isUploading ? documentType : prev.uploading,
      uploadError: {
        ...prev.uploadError,
        [documentType]: null,
      },
    }));
  };

  const handleFileUpload = async (files: FileList, documentType: string) => {
    resetDocumentStateErrors(documentType, true);

    const totalFileSize = Array.from(files).reduce(
      (acc, file) => acc + file.size,
      0,
    );
    if (totalFileSize > MAX_COMBINED_SIZE_OF_FILE_UPLOADS) {
      setDocumentState((prev) => ({
        ...prev,
        uploadError: {
          ...prev.uploadError,
          [documentType]: `Total combined size of all files exceeds 20MB. Please reduce the number of files to upload and try again. ${EMAIL_DOCUMENT_TO_US_COPY}`,
        },
        uploading: null,
      }));
      Sentry.captureMessage(`Total file size error: ${totalFileSize}`);
      return;
    }

    try {
      await Promise.all(
        Array.from(files).map((file) =>
          uploadDocument({
            variables: {
              caseUuid: urlCaseId,
              kind: documentType,
              file,
            },
          }),
        ),
      );
    } catch (error) {
      setDocumentState((prev) => ({
        ...prev,
        uploadError: {
          ...prev.uploadError,
          [documentType]: handleError(
            error,
            `Failed to upload documents. ${EMAIL_DOCUMENT_TO_US_COPY}`,
          ),
        },
      }));
    } finally {
      await updateExistingFilesState();
      setDocumentState((prev) => ({ ...prev, uploading: null }));
    }
  };

  if (!thisCase && caseDocumentsLoading) {
    return <MainStateContainer title="Case Documents" loading />;
  }

  if (documentState.documentsError) {
    return <MainStateContainer title="Case Documents" error />;
  }

  return (
    <PageContent title="Application Documents">
      <div className="flex w-full flex-col md:flex-row md:space-x-8">
        <Sidebar title="Application">
          <DocsSidebar thisCase={thisCase} application={application} />
        </Sidebar>

        <div className="md:basis-3/4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 md:p-8">
            <div className="flex flex-col space-y-4">
              <FormTitle left border title="Documents" mb={false} />

              <p>
                Please upload the required documents below. Documents marked
                with an asterisk (*) require Abound - specific forms. You can
                download these, here:{" "}
                <a
                  href={`${APP_URLS.RESOURCES}/application`}
                  target="_blank"
                  rel="noreferrer"
                  className="link underline"
                >
                  Application Resources
                </a>
                .
              </p>

              <div className="space-y-6 pt-6">
                <ResourceList showLink resources={linkDocumentTypes} />

                <UploadedFileList
                  files={caseDocuments?.data?.case?.documents ?? []}
                />

                {documentTypes.map((documentType) => {
                  const remainingSlots =
                    documentType.maxFiles -
                    getExistingFiles(documentType.type).length;

                  return (
                    <div
                      key={documentType.type}
                      className="border-b bg-white pb-6 last:border-b-0 last:pb-0"
                    >
                      <div className="mt-4 flex items-start justify-between first:mt-0">
                        <div>
                          <h2 className="text-lg font-medium">
                            {documentType.label}
                          </h2>
                          <p className="mt-1 text-sm text-gray-600">
                            {documentType.description}
                          </p>
                        </div>
                      </div>

                      {documentState.uploadError[documentType.type] && (
                        <FileError
                          error={documentState.uploadError[documentType.type]}
                        />
                      )}

                      <UploadInput
                        documentType={documentType.type}
                        remainingSlots={remainingSlots}
                        uploadingDocumentType={documentState.uploading}
                        onFileUpload={handleFileUpload}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContent>
  );
};

export default CaseDocuments;
